import { z } from 'zod';
/**
 * What kind of record a stored file is (ADR-009 §1).
 *
 * A CLOSED vocabulary, following the `gateSchema` precedent: the category names are code, and
 * which requirement uses which is data. It began as the SharePoint routing-target enum
 * (`routing/targets.ts`, #49), which replaced free text in three unlinked places that validated
 * nothing. Files now live in Azure rather than SharePoint libraries, but the vocabulary and its
 * segregation rule outlived the destination, so the eleven values and their labels are kept
 * unchanged — they are already on stored step definitions and instances.
 */
export const fileCategorySchema = z.enum([
    'recruitment-onboarding',
    'employee-records',
    'training-competency',
    'offboarding-separations',
    'policies-compliance',
    'compensation-benefits',
    'performance-employee-relations',
    'templates-forms',
    'strategic-planning-reports',
    /**
     * Form I-9 (HR 4.8.1, HR 4.9.1). Its retention clock differs from the personnel file's —
     * 3 years after hire or 1 after separation, whichever is later.
     */
    'segregated-i9',
    /**
     * Employee health information: TB results, Hep B offer and response, immunisation
     * status, exposure records, fitness-for-duty. Kept in separate, confidential medical files
     * (ADA; catalog §3.2, open question Q39).
     */
    'segregated-health',
]);
/**
 * The segregated categories, named once.
 *
 * `segregated` is DERIVED from the category and is never stored beside it. It was stored
 * three times before, and all three disagreed: `hepb-info-sheet-issued` was flagged
 * segregated while filed as training, even though the catalog says in as many words that it
 * is "a training record, not a medical record". A flag that can contradict the category it
 * describes is a bug waiting to be believed.
 */
const SEGREGATED = new Set(['segregated-i9', 'segregated-health']);
export function isSegregatedCategory(category) {
    return SEGREGATED.has(category);
}
/**
 * The file category a PINNED step config names — under either of the two keys that name one.
 *
 * Most step types call it `target` (document-upload, pdf-form, date-credential, acknowledgment,
 * candidate-application). `hr-record` calls it `fileCategory`, set from the requirement's own
 * metadata when HR records evidence directly (#188). Two readers assumed `target` was the only
 * name, and both were silently wrong for every hr-record:
 *
 *   - `isSegregatedInstance` (people/timeline.ts) classified every HR direct record of an I-9 or a
 *     health requirement as unsegregated, so the timeline and the review card emitted
 *     `segregated: false`, `fileWithheld: false` and the `storedFileId` to a viewer without
 *     `files.restricted.read`. The BYTES stayed protected — `fileContentHandler` gates those on the
 *     stored file's own category — but the classification and the id did not.
 *   - `filesToSegregatedCategory` (steps/contract.ts) suppresses `profileFields` for a segregated
 *     step so segregated data cannot be copied onto the person profile. `hr-record` declares no
 *     `profileFields` today, so nothing leaked through it — but the guard was written generically
 *     "so it covers a type that does not exist yet", and reading one key defeated exactly that.
 *
 * Read here, once, so a third reader cannot repeat it. `target` wins if both are present: it is the
 * key inside every stored definition's hashed content and cannot be renamed without versioning all
 * of them, which is why there are two names rather than one.
 */
export function configFileCategory(config) {
    if (typeof config !== 'object' || config === null)
        return null;
    const named = config;
    const parsed = fileCategorySchema.safeParse(named.target ?? named.fileCategory);
    return parsed.success ? parsed.data : null;
}
/**
 * WHERE A SIGNED OFFER MAY BE FILED — every category that is not segregated.
 *
 * An offer letter is never an I-9 or a health record. `defaultOfferTarget` used to be a bare
 * `fileCategorySchema` whose dropdown listed all eleven, so an admin could point signed offers at
 * the restricted account. A runtime guard refused it on one of the two paths that read the setting,
 * AFTER HR had uploaded the letter and typed the note, with a message about an agency-wide setting
 * they cannot act on — and the portal-offer path had no guard at all.
 *
 * Making the state unreachable fixes both readers and needs no one to remember. DERIVED, so adding
 * a segregated category excludes it here without a second list to keep in step.
 */
export const offerFilingCategorySchema = z.enum(fileCategorySchema.options.filter((category) => !isSegregatedCategory(category)));
/** Human-facing names, for HR screens and the generated catalog summary. */
export const fileCategoryLabels = {
    'recruitment-onboarding': 'Recruitment & Onboarding',
    'employee-records': 'Employee Records',
    'training-competency': 'Training & Competency',
    'offboarding-separations': 'Offboarding & Separations',
    'policies-compliance': 'Policies & Compliance',
    'compensation-benefits': 'Compensation & Benefits',
    'performance-employee-relations': 'Performance & Employee Relations',
    'templates-forms': 'Templates & Forms',
    'strategic-planning-reports': 'Strategic Planning & Reports',
    'segregated-i9': 'Segregated — I-9',
    'segregated-health': 'Segregated — employee health',
};
/**
 * The two protected storage accounts (ADR-009 §1), split by trust boundary. The quarantine
 * account is deliberately absent: nothing in it is a committed file.
 */
export const fileStoreSchema = z.enum(['records', 'restricted']);
export const fileContainerSchema = z.enum(['personnel', 'templates', 'health', 'i-9']);
/**
 * Where a category's files are committed. Pure, and the ONLY place a category meets a
 * container name.
 *
 * Written as a switch over the segregated values rather than a lookup table so that the
 * default — the ordinary personnel file — can only ever be reached by a category that is not
 * segregated. tests/file-vocabulary.test.ts proves that over the whole vocabulary.
 */
export function resolveFileStore(category) {
    switch (category) {
        case 'segregated-i9':
            return { store: 'restricted', container: 'i-9' };
        case 'segregated-health':
            return { store: 'restricted', container: 'health' };
        default:
            return { store: 'records', container: 'personnel' };
    }
}
/**
 * Controlled templates (PDF form sources). Reachable only through this explicit constant, used
 * by the template path — never from a requirement's category, so no category, including
 * `templates-forms`, can put a person's record among the templates.
 */
export const TEMPLATE_FILE_LOCATION = { store: 'records', container: 'templates' };
/** The one category a controlled template is recorded under. */
export const TEMPLATE_FILE_CATEGORY = 'templates-forms';
/**
 * Whether a stored (category, location) pair is one the app could have written: the
 * category's own location, or — for `templates-forms` only — the template location. Any other
 * category in `templates` would be a person's record hidden among the agency's blank forms.
 */
export function isFileLocationFor(category, location) {
    const resolved = resolveFileStore(category);
    if (location.store === resolved.store && location.container === resolved.container)
        return true;
    return (category === TEMPLATE_FILE_CATEGORY &&
        location.store === TEMPLATE_FILE_LOCATION.store &&
        location.container === TEMPLATE_FILE_LOCATION.container);
}
//# sourceMappingURL=categories.js.map